import {Component, EventEmitter, OnInit, Output, ViewEncapsulation} from '@angular/core';
import {UserSavedRequestService} from '../../../../../../services/apiServices/business/user-saved-request.service';
import {ConfirmationService} from 'primeng/api';
import {TranslateService} from '@ngx-translate/core';
import {CommonService} from '../../../../../../services/apiServices/business/common.service';

/**
 * Component that handle retrieving saved request and can delete them
 */
@Component({
  selector: 'app-get-or-remove-or-schedule-saved-request',
  templateUrl: './get-or-remove-or-schedule-saved-request.html',
  styleUrls: ['./get-or-remove-or-schedule-saved-request.scss'],
  encapsulation: ViewEncapsulation.None
})
export class GetOrRemoveOrScheduleSavedRequestComponent implements OnInit {

  /**
   * Selected request output
   */
  @Output() public selectedRequest: EventEmitter<any>;

  /**
   * the list of custom request saved by user
   */
  public listOfSavedRequest: any[];

  /**
   * the active request from listOfSavedRequest
   */
  public activeRequest: any;

  /**
   * Title of message displayed when the request suppresion worked
   */
  private successRequestTitle: string;

  /**
   * Message displayed when the request suppresion worked
   */
  private successRequestMessage: string;

  /**
   * Title of message displayed when the request suppresion dit not worked
   */
  private errorRequestTitle: string;

  /**
   * Message displayed when the request suppresion dit not worked
   */
  private errorRequestMessage: string;

  /**
   * label for default request choice
   */
  private defaultRequestLabel: string;

  /**
   * Body of the default confirmation message (for request deletion)
   */
  private suppressConfirmationMessage: string;

  /**
   * if the schedule part of saved request should be displayed
   */
  public displayScheduling: boolean;

  /**
   * Title for confirmation modal
   */
  private confirmationHeaderTitle: string;

  /**
   *
   * default constructor, instantiate variables and use various services described below
   *
   * @param {UserSavedRequestService} userSavedRequestService - handle requests saved by user
   * @param {ConfirmationService} confirmationService - handle confirmation for suppression
   * @param commonService
   * @param {TranslateService} translate - handle translation for component
   */
  constructor(private userSavedRequestService: UserSavedRequestService, private confirmationService: ConfirmationService,
              private commonService: CommonService, private translate: TranslateService) {
    this.listOfSavedRequest = [];
    this.selectedRequest = new EventEmitter<any>();
  }

  /**
   * Init variables value after constructor
   */
  public ngOnInit(): void {
    this.displayScheduling = false;
    this.getTradForComponent();
    this.addDefaultNewRequest();
    this.handleSavedRequestSubscription();
    this.userSavedRequestService.callRequestUpdate();
  }

  /**
   * Add the new Request (not provided by back-office)
   */
  private addDefaultNewRequest(): void {
    this.listOfSavedRequest.push({label: this.defaultRequestLabel, value: {requestName: this.defaultRequestLabel}});
    this.activeRequest = this.listOfSavedRequest[0].value;
    this.emitActiveRequest();
  }

  /**
   * Build the list of previously saved requests
   */
  private handleSavedRequestSubscription(): void {
    this.userSavedRequestService.listOfSavedRequest.subscribe(res => {
      this.listOfSavedRequest = [];
      this.addDefaultNewRequest();
      if (res && res.body && res.body.listObjCustomTrackingCriteriaNamed) {
        for (const savedRequest of res.body.listObjCustomTrackingCriteriaNamed) {
          const name = savedRequest.sName;
          const value = {
            requestName: name,
            simpleFilters: savedRequest.objTrackingCriteria,
            advancedFilters: this.getOnlyInterestingPartFromAdvancedFilters(savedRequest.objTrackingCriteria.objCriteria),
            scheduleFilters: {
              sExecuteScheduling: savedRequest.sExecuteScheduling,
              sMail: savedRequest.sMail,
              sParamBatch: savedRequest.sParamBatch,
              bMailNoActivity: savedRequest.bMailNoActivity,
              bBatch: savedRequest.bBatch,
              bCsvExtraction: savedRequest.bCsvExtraction
            }
          };

          if (savedRequest.listDataRef) {
            value['scheduleFilters']['listDataSentRef'] = savedRequest.listDataRef.listDataSentRef;
          }
          if (name) {
            this.listOfSavedRequest.push({label: name, value: value});
          }
        }
      }

    });
  }

  /**
   * filter data for advanced filters, no need to get every available information
   * @param {any[]} advancedFilters - list of advanced filters
   * @return {any[]} - simplified advanced filter
   */
  private getOnlyInterestingPartFromAdvancedFilters(advancedFilters: any[]): any[] {
    const result = [];
    if (advancedFilters) {
      for (const advancedFilter of advancedFilters) {
        result.push({sNameId: advancedFilter.sNameId, sCriteriaTrackingValue: advancedFilter.sCriteriaTrackingValue});
      }
    }
    return result;
  }

  /**
   * obtain translation from TranslateService
   */
  private getTradForComponent(): void {
    this.translate.get('TOASTER.SUCCESS_MESSAGE_TITLE').subscribe((translation: string) => {
      this.successRequestTitle = translation;
    });
    this.translate.get('TOASTER.DELETE_REQUEST_OK').subscribe((translation: string) => {
      this.successRequestMessage = translation;
    });
    this.translate.get('TOASTER.ERROR_MESSAGE_TITLE').subscribe((translation: string) => {
      this.errorRequestTitle = translation;
    });
    this.translate.get('TOASTER.ERROR_MESSAGE_MESSAGE').subscribe((translation: string) => {
      this.errorRequestMessage = translation;
    });
    this.translate.get('SAVED_REQUEST.GET.OR.REMOVE.SAVED.REQUEST_COMPONENT.CONFIRMATION_MESSAGE_DELETE').subscribe((translation: string) => {
      this.suppressConfirmationMessage = translation;
    });
    this.translate.get('SAVED_REQUEST.NEW_REQUEST_LABEL').subscribe((translation: string) => {
      this.defaultRequestLabel = translation;
    });
    this.translate.get('SAVED_REQUEST.GET.OR.REMOVE.SAVED.REQUEST_COMPONENT.CONFIRMATION_MODAL_TITLE').subscribe((translation: string) => {
      this.confirmationHeaderTitle = translation;
    });
  }

  /**
   * delete the active request
   */
  public deleteSavedRequest(): void {

    this.confirmationService.confirm({
      message: this.suppressConfirmationMessage + this.activeRequest.requestName + '?',
      header: this.confirmationHeaderTitle,
      accept: () => {
        this.userSavedRequestService.deleteSavedRequest(this.activeRequest.requestName).subscribe(res => {
          if (res) {
            this.commonService.addToast({
              life: 3000,
              severity: 'success',
              summary: this.successRequestTitle,
              detail: this.successRequestMessage
            });
          } else {
            this.commonService.addToast({
              life: 3000,
              severity: 'error',
              summary: this.errorRequestTitle,
              detail: this.errorRequestMessage
            });
          }
        });
      }
    });
  }

  /**
   * emit change when active request change
   */
  public emitActiveRequest(): void {
    this.selectedRequest.emit(this.activeRequest);
  }

  /**
   * Determine if a request can be deleted (not possible if it's default value)
   * @return {boolean} - the result
   */
  public requestCanBeDeleted(): boolean {
    return this.activeRequest.requestName !== this.defaultRequestLabel;
  }
  public showSchedulingModal(): void {
    this.displayScheduling = true;
  }
}
